import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import itineraryItemObject from '@/entities/itineraryItem/interface'

import {
  ALL_ITINERARIES_QUERY,
  SINGLE_ITINERARY_QUERY,
  ITINERARIES_DEPENDENTS_QUERY,
  CREATE_ITINERARY_MUTATION,
  UPDATE_ITINERARY_MUTATION,
  DELETE_ITINERARY_MUTATION,
  FORCE_DELETE_ITINERARY_MUTATION,
  ADD_ITINERARY_ITEMS_MUTATION,
  REMOVE_ITINERARY_ITEMS_MUTATION,
  CREATE_ITINERARY_ITEMS_MUTATION,
  DELETE_ITINERARY_ITEMS_MUTATION,
  UPDATE_ITINERARY_ITEMS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const itineraryRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_ITINERARY_QUERY, variables).then((data: any) => {
      return hydrateObject(data.itinerary)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_ITINERARIES_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.itineraries)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_ITINERARY_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.itinerary),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_ITINERARIES_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.itineraries),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(ITINERARIES_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([ITINERARIES_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_ITINERARY_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.itinerary)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_ITINERARY_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.itinerary)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_ITINERARY_MUTATION, variables).then((data: any) => {
      return data.deleteItinerary
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_ITINERARY_MUTATION, variables).then((data: any) => {
      return data.forceDeleteItinerary
    })
  },

  assignItems: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        itemIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_ITINERARY_ITEMS_MUTATION, variables)
  },

  unassignItems: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        itemIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_ITINERARY_ITEMS_MUTATION, variables)
  },

  createItems: async (id: string, items: Array<itineraryItemObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        items: items,
      },
    }
    return graphQLClient.request(CREATE_ITINERARY_ITEMS_MUTATION, variables)
  },

  deleteItems: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        itemIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_ITINERARY_ITEMS_MUTATION, variables)
  },

  updateItemsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        itemIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_ITINERARY_ITEMS_ORDER_MUTATION, variables)
  },
}

export default itineraryRepository
